import { useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Slider, Typography } from '@mui/material'
import { ArrowForwardIos } from '@mui/icons-material'
import { Trans, useTranslation } from 'react-i18next'
import {
  RESERVE_DEFAULT_STEP, RESERVE_FACTORS, ReserveBand, ReserveFactor,
  factorToStep, reserveBand, reserveStepLabel, reserveWarning, stepToFactor,
} from '../utils/reserve-tuning'

/** Slider colours per band, the same as the algorithm fine-tuning sliders. */
const BAND_COLORS: Record<ReserveBand, string> = {
  green: '#09E7A4',
  yellow: '#F4E356',
}

const MARKS = RESERVE_FACTORS.map((_, i) => ({ value: i }))

// The rail shows the bands of all steps: the green middle reaches halfway to
// the first yellow step on each side.
const LAST_STEP = RESERVE_FACTORS.length - 1
const GREEN_FROM = ((RESERVE_DEFAULT_STEP - 1.5) / LAST_STEP) * 100
const GREEN_TO = ((RESERVE_DEFAULT_STEP + 1.5) / LAST_STEP) * 100
const RAIL = `linear-gradient(to right, ${BAND_COLORS.yellow} ${GREEN_FROM}%, ${BAND_COLORS.green} ${GREEN_FROM}%, ` +
  `${BAND_COLORS.green} ${GREEN_TO}%, ${BAND_COLORS.yellow} ${GREEN_TO}%)`

const MARK_COLORS = Object.fromEntries(MARKS.map(({ value }) => [
  `& .MuiSlider-mark[data-index="${value}"]`, { backgroundColor: BAND_COLORS[reserveBand(value)] },
]))

const WARNINGS = {
  'self-use': 'By making this setting, I am aware that the reserve settlement may drop significantly.',
  reserve: 'By making this setting, I am aware that the self-use benefit may drop significantly.',
}

export interface ReserveTuningProps {
  /** Stored `bid_price_factor` (unset = 1.00). */
  factor: unknown
  onChange: (factor: ReserveFactor) => void
}

/**
 * "Reserve participation fine-tuning" accordion for reserve batteries: one
 * 7-step slider between more self-use benefit and more reserve income,
 * stored as the bid price factor the FCR service applies. Collapsed by
 * default.
 */
export const ReserveTuning = ({ factor, onChange }: ReserveTuningProps): JSX.Element => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const step = factorToStep(factor)
  const color = BAND_COLORS[reserveBand(step)]
  const warning = reserveWarning(step)

  return <Accordion disableGutters expanded={expanded} onChange={(_, open) => setExpanded(open)} sx={{ mt: 3 }} data-cy="accordion-Reserve participation fine-tuning">
    <AccordionSummary expandIcon={<ArrowForwardIos data-cy="accordion-icon-Reserve participation fine-tuning" />}>
      <Trans>Reserve participation fine-tuning</Trans>
    </AccordionSummary>
    <AccordionDetails data-cy="box-Reserve participation fine-tuning">
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', mx: 1, mt: 1 }}>
        <Typography variant="caption" sx={{ color: 'grey.400' }}>{t('more self-use benefit')}</Typography>
        <Typography variant="caption" sx={{ color: 'grey.400', textAlign: 'right' }}>{t('more reserve income')}</Typography>
      </Box>
      <Box sx={{ mx: 2, mt: 3.5 }}>
        <Slider
          data-cy="slider-Reserve participation"
          size="small"
          min={0}
          max={LAST_STEP}
          step={1}
          marks={MARKS}
          value={step}
          track={false}
          valueLabelDisplay="on"
          valueLabelFormat={reserveStepLabel}
          onChange={(_, s) => onChange(stepToFactor(s as number))}
          sx={{
            '& .MuiSlider-rail': { background: RAIL, opacity: 0.6 },
            // Explicit px strings: a bare `width: 1` means 100 % in sx.
            '& .MuiSlider-mark': { opacity: 0.6, height: '4px', width: '1px' },
            ...MARK_COLORS,
            [`& .MuiSlider-mark[data-index="${RESERVE_DEFAULT_STEP}"]`]: { backgroundColor: BAND_COLORS.green, height: '10px', opacity: 1 },
            '& .MuiSlider-thumb': { color: '#FFF', borderColor: color, borderWidth: 2, borderStyle: 'solid' },
            '& .MuiSlider-valueLabel': { backgroundColor: color, color: '#000', fontWeight: 600, fontSize: '0.75rem' },
          }}
        />
      </Box>

      {warning && <Box
        data-cy="box-Reserve tuning warning"
        sx={{
          mx: 1,
          mt: 2,
          p: 1.5,
          borderRadius: '0.625rem',
          border: '1px solid #F4E356',
          backgroundColor: 'rgba(244, 227, 86, 0.08)',
        }}
      >
        <Typography variant="body2">{t(WARNINGS[warning])}</Typography>
      </Box>}
    </AccordionDetails>
  </Accordion>
}
